import {
  Component,
  ElementRef, Input,
  OnChanges, SimpleChanges,
  ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions } from '@ngrx/effects';
import { Comment } from '../../../models/comment';
import { setMapHandleKeyboard } from '../../../features/cesium/cesium.actions';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@jsverse/transloco';
import {
  selectAllUsers,
  selectUser
} from '../../../features/user/user.selectors';
import {
  UploadButtonComponent
} from '../upload-button/upload-button.component';
import {
  selectConfigItemDetailExpanded
} from '../../../features/config/config.selectors';
import { User } from '../../../models/user';


@Component({
    selector: 'history-channel',
    templateUrl: './history-channel.component.html',
    styleUrls: ['./history-channel.component.scss'],
    standalone: false
})
export class HistoryChannelComponents implements OnChanges {

  @Input({required: true}) itemId: string | undefined;
  comments: Array<Comment> = [];
  selectedFiles: File[] = [];
  @ViewChild('editor') textareaRef: ElementRef<HTMLTextAreaElement> | undefined;
  @ViewChild('commentList') commentsRef: ElementRef<HTMLDivElement> | undefined;
  @ViewChild(UploadButtonComponent) uploadComponent!: UploadButtonComponent;
  readonly user$ = this.store.select(selectUser);
  readonly users$ = this.store.select(selectAllUsers);
  readonly isExpanded$ = this.store.select(selectConfigItemDetailExpanded);
  isPublishing: boolean = false;
  isLoading: boolean = false;

  allUsers: User[] = [];
  filteredMentionUsers: User[] = [];

  mentionVisible = false;
  mentionStartIndex: number | null = null;
  mentionQuery = '';


  constructor(protected store: Store,
              protected actions: Actions,
              protected toastr: ToastrService,
              protected translocoService: TranslocoService) {
    this.initHandleComments();
  }

  initHandleComments(): void {

  }

  loadComments(): void {

  }

  onClickAddComment(fileNames: string[]): void {

  }

  submit(uploadComponent: UploadButtonComponent): void {
    // si pas de fichiers → commentaire direct
    if (!uploadComponent.selectedFiles.length) {
      this.isPublishing = false;
      this.onClickAddComment([]);
      return;
    }

    // sinon → lancer upload
    this.isPublishing = true;
    uploadComponent.startUpload();
  }

  clearComments() {
    this.comments = [];
  }

  clearEditor() {
    if (this.textareaRef) {
      this.textareaRef.nativeElement.value = '';
      this.autoResize(this.textareaRef.nativeElement);
    }
    this.selectedFiles = [];
    if (this.uploadComponent) {
      this.uploadComponent.reset();
    }
  }

  scrollToBottom(): void {
    setTimeout(() => {
      if (this.commentsRef) {
        this.commentsRef.nativeElement.scrollTop = this.commentsRef.nativeElement.scrollHeight;
      }
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.clearEditor();
    this.clearComments();
    this.loadComments();
  }

  onHandleFocus(): void {
    this.store.dispatch(setMapHandleKeyboard({mapHandleKeyboard: false}));
  }

  onFocusLeave(): void {
    this.store.dispatch(setMapHandleKeyboard({mapHandleKeyboard: true}));
  }

  autoResize(textarea: HTMLTextAreaElement): void {
    const maxHeight = 120;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      this.selectedFiles = [];
      return;
    }

    this.selectedFiles = Array.from(input.files);
    input.value = '';
  }

  onUploadCompleted(fileNames: string[]): void {
    this.onClickAddComment(fileNames);
    this.isPublishing = false;
    this.uploadComponent.reset();
  }

  onUploadFailed(error: any): void {
    this.isPublishing = false;
    this.toastr.error(this.translocoService.translate('add_comment_failure'));
  }

  onContentHeightChanged(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.scrollToBottom();
      });
    });
  }

  private usersSub = this.users$.subscribe(users => {
    this.allUsers = users ?? [];
  });

  onEditorInput(editor: HTMLTextAreaElement): void {
    this.autoResize(editor);

    const cursorPosition = editor.selectionStart;
    const textBeforeCursor = editor.value.slice(0, cursorPosition);

    const match = this.findCurrentMention(textBeforeCursor);

    if (!match) {
      this.closeMention();
      return;
    }

    this.mentionStartIndex = match.index;
    this.mentionQuery = match.query.toLowerCase();

    this.filteredMentionUsers = this.allUsers
      .filter(user =>
        user.username?.toLowerCase().startsWith(this.mentionQuery)
      )
      .slice(0, 8);

    this.mentionVisible = this.filteredMentionUsers.length > 0;
  }

  private findCurrentMention(textBeforeCursor: string): { index: number; query: string } | null {
    /**
     * Match un @ en début de texte ou précédé par un espace.
     * Refuse les emails du type test@domain.com.
     *
     * Exemples valides :
     * "@j"
     * "hello @john"
     *
     * Exemples ignorés :
     * "john@email.com"
     */
    const match = /(^|\s)@([a-zA-Z0-9_.-]*)$/.exec(textBeforeCursor);

    if (!match) {
      return null;
    }

    const atIndex = textBeforeCursor.lastIndexOf('@');

    return {
      index: atIndex,
      query: match[2],
    };
  }

  insertMention(user: User, editor: HTMLTextAreaElement): void {
    if (this.mentionStartIndex === null) {
      return;
    }

    const cursorPosition = editor.selectionStart;
    const beforeMention = editor.value.slice(0, this.mentionStartIndex);
    const afterMention = editor.value.slice(cursorPosition);

    const mention = `@${user.username} `;

    editor.value = beforeMention + mention + afterMention;

    const newCursorPosition = beforeMention.length + mention.length;

    editor.focus();
    editor.setSelectionRange(newCursorPosition, newCursorPosition);

    this.closeMention();
    this.autoResize(editor);
  }

  private closeMention(): void {
    this.mentionVisible = false;
    this.mentionStartIndex = null;
    this.mentionQuery = '';
    this.filteredMentionUsers = [];
  }

}
